import AddElement from '../../generic/smartAddElement/AddElement'
import { apiDelete, apiPut, apiPost } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { IconButton } from '@mui/material'
import TextSnippetOutlined from '@mui/icons-material/TextSnippetOutlined'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import React, { useRef, useState } from 'react'
import moment from 'moment/moment.js'
import Document from '../document/Document'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import QrCodeGeneratorDialog, { buildAvailableFields } from '../../generic/qr/QrCodeGeneratorDialog'

const Fixture = (props) => {
  // Historia 170 (J4b, S103) — `preselectedRecord` additive, optional, inert when absent (every OTHER
  // caller): `FixtureReference.jsx` forwards this when a document repository row's "Open associated
  // record" preselects a FIXTURE inside this reference.
  const { parameter, endpoint, referenceName, hideColumns = false, onRowClick, showQrAction = false, preselectedRecord = null } = props
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  const [showDocument, setShowDocument] = useState(false)
  const [showQr, setShowQr] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [rowFixture, setRowFixture] = useState(null)
  const [editing, setEditing] = useState(false)

  const columns = [
    {
      name: t_('Code'),
      field: 'code',
      edit: false,
      charLimit: 60,
      mandatory: true,
      unique: true
    },
    {
      name: t_('Name'),
      field: 'name',
      edit: true,
      charLimit: 80,
      mandatory: true,
      unique: false
    },
    {
      name: t_('Description'),
      field: 'description',
      charLimit: 200
    },
    {
      name: t_('Location'),
      field: 'location.name',
      readOnly: true,
      hide: hideColumns
    },
    {
      name: t_('Shelf'),
      field: 'shelveStorageConfiguration.shelve',
      readOnly: true,
      hide: hideColumns
    },
    {
      name: t_('Row'),
      field: 'shelveStoragePosition.position',
      readOnly: true,
      hide: hideColumns
    },
    {
      name: t_('Column'),
      field: 'shelveStoragePosition.depth',
      readOnly: true,
      hide: hideColumns
    },
    {
      name: t_('Entry date'),
      field: 'entryDate',
      type: 'dateTimePicker',
      readOnly: false,
      numeric: true,
      mandatory: true
    }
  ]

  const formatDatePickerDate = (rows) => {
    rows.forEach((rawMaterial) => {
      const date = moment(rawMaterial.entryDate).format('YYYY-MM-DD[T]HH:mm:ssZ')
      rawMaterial.entryDate = date
    }

    )

    return rows
  }

  const actions = {
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        disabled: (editing || !isUserAuthorized('DOCUMENTS_FIXTURE')),
        onClick: (row) => {
          setRowFixture(row)
          setShowDocument(true)
        }
      },
      ...(showQrAction
        ? [{
            name: 'Generate QR',
            render: (row) => (
              <IconButton>
                <QrCode2Icon />
              </IconButton>
            ),
            disabled: editing || !isUserAuthorized('GENERATE_QR_CODE'),
            onClick: (row) => {
              setRowFixture(row)
              setShowQr(true)
            }
          }]
        : [])
    ],
    edit: {
      fetchFunction: (row) => {
        setEditing(false)
        if (row.entryDate) {
          const date = moment(row.entryDate).format('YYYY-MM-DD[T]HH:mm:ssZ')
          row.entryDate = date
        }
        return apiPut('fixtures', row)
      },
      disabled: !isUserAuthorized('EDIT_FIXTURE')
    },
    delete: {
      fetchFunction: (row) => {
        if (row.length === 1) {
          return apiDelete('fixtures/' + row[0].id, row)
        } else {
          row = formatDatePickerDate(row)
          return apiDelete('fixturesMulti', row)
        }
      },
      disabled: !isUserAuthorized('DELETE_FIXTURE')
    }
  }

  const defaultToolbar = {
    renderSticky: {
      fetchFunction: (extraFunctions) => {
        const handleAlert = extraFunctions.showAlert
        return (
          <AddElement
            parameter={parameter}
            endpoint='fixtures'
            disable={!isUserAuthorized('ADD_FIXTURE')}
            referenceName={referenceName}
            title={t_('Add fixtures')}
            onAlert={handleAlert}
            handleAlert={extraFunctions.showAlert}
          />
        )
      }
    }
  }

  const disableRowActions = useRef(false)

  const dataFetch = async (pagination) => {
    const fixtures = await apiPost(endpoint + '/' + parameter, pagination)
    return fixtures
  }

  return (
    <div>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<Document reference={rowFixture} resourceType='FIXTURE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showDocument}
      />
      {showQr && (
        <QrCodeGeneratorDialog
          open={showQr}
          onClose={() => setShowQr(false)}
          entityType='Fixture'
          entityLabel={t_('Fixture')}
          instance={rowFixture}
          availableFields={buildAvailableFields(columns, rowFixture)}
        />
      )}
      <SmartMateriaUITable
        tableTuneUniqueKey='fixture'
        columns={columns}
        title={referenceName}
        dataFetch={dataFetch}
        actions={actions}
        preselectedRecord={preselectedRecord}
        multipleSelection={!hideColumns}
        deleteMultipleSelectionDisabled={!isUserAuthorized('DELETE_FIXTURE')}
        dense
        editingRow={(event) => setEditing(event)}
        toolbar={defaultToolbar}
        setDisableRowActions={(event) => { disableRowActions.current = event }}
        onRowClick={(row) => {
          if (onRowClick && typeof onRowClick === 'function' && row.id) {
            onRowClick(row)
          }
        }}
        pageable
      />
    </div>
  )
}

export default Fixture
